import type { Adaptors } from '@reactvideoeditor/react-video-editor/types';

type ImageAdaptor = NonNullable<Adaptors['images']>[number];
type VideoAdaptor = NonNullable<Adaptors['video']>[number];
type MediaSearchParams = Parameters<ImageAdaptor['search']>[0];
import { pickPexelsVideoUrl } from '@/rve/aspectlyPexelsUtils';
import { searchStock } from '@/rve/stockApi';
import type { StockImageItem, StockVideoItem } from '@/rve/stockTypes';

export interface AspectlyPexelsAdaptorDeps {
  searchStock: typeof searchStock;
}

const defaultPexelsDeps: AspectlyPexelsAdaptorDeps = {
  searchStock,
};

const PEXELS_ADAPTOR_META = {
  name: 'pexels',
  displayName: 'Pexels',
  description: 'Free stock via Aspectly',
  requiresAuth: false,
} as const;

type VideoQuality = StockVideoItem['videoFiles'][number]['quality'];

/** Org-scoped Pexels image search (backend proxy, CDN URLs in results). */
export function createAspectlyPexelsImageAdaptor(
  orgId: number,
  deps: AspectlyPexelsAdaptorDeps = defaultPexelsDeps,
): ImageAdaptor {
  return {
    ...PEXELS_ADAPTOR_META,
    supportedTypes: ['image'],
    async search(params: MediaSearchParams) {
      const result = await deps.searchStock(orgId, 'image', {
        query: params.query,
        page: typeof params.page === 'number' ? params.page : 1,
        perPage: params.perPage,
        orientation: params.orientation,
      });
      return {
        items: result.items,
        totalCount: result.totalCount,
        hasMore: result.hasMore,
        nextPage: result.nextPage,
      };
    },
    getImageUrl(image: StockImageItem, size: keyof StockImageItem['src'] = 'original') {
      return image.src[size] ?? image.src.original;
    },
  } as ImageAdaptor;
}

/** Org-scoped Pexels video search (backend proxy, CDN URLs in results). */
export function createAspectlyPexelsVideoAdaptor(
  orgId: number,
  deps: AspectlyPexelsAdaptorDeps = defaultPexelsDeps,
): VideoAdaptor {
  return {
    ...PEXELS_ADAPTOR_META,
    supportedTypes: ['video'],
    async search(params: MediaSearchParams) {
      const result = await deps.searchStock(orgId, 'video', {
        query: params.query,
        page: typeof params.page === 'number' ? params.page : 1,
        perPage: params.perPage,
        orientation: params.orientation,
      });
      return {
        items: result.items,
        totalCount: result.totalCount,
        hasMore: result.hasMore,
        nextPage: result.nextPage,
      };
    },
    getVideoUrl(video: StockVideoItem, quality: VideoQuality = 'hd') {
      return pickPexelsVideoUrl(video, quality);
    },
    getThumbnailUrl(video: StockVideoItem) {
      return video.thumbnail;
    },
  } as VideoAdaptor;
}
